import { RulebookPage } from "@/components/RulebookPage";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

export function LifeAndDeath() {
  return (
    <RulebookPage title="Life & Death">
      <h2 className="text-xl font-semibold mt-6 mb-2">Resting &amp; Healing</h2>
      <p className="text-sm text-muted-foreground mb-3">
        During the game your character will experience Short and Long Rests.
      </p>

      <Card className="mb-3">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Short Rest</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Lunch and dinner are Short Rests. Your character will regain 1 MP and
          1 HP, though you must eat and drink to gain this benefit.
          Alternatively, you may spend 1 silver coin to regain all of your
          expended HP and MP, as your character treats themselves to the very
          best refreshments available.
        </CardContent>
      </Card>

      <Card className="mb-3">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Long Rest</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          A night&apos;s sleep is a Long Rest. Every dawn, your character
          regains all of their HP and MP, though you must sleep to gain this
          benefit. Long Rests also cure the Poisoned condition.
        </CardContent>
      </Card>

      <hr className="my-6 border-border" />

      <h2 className="text-xl font-semibold mt-6 mb-2">Bleeding Out</h2>
      <p className="text-sm text-muted-foreground mb-3">
        When you are reduced to 0 HP, you begin Bleeding Out.
      </p>
      <ul className="list-disc list-inside text-sm text-muted-foreground mb-3 space-y-1">
        <li>You have 5 minutes before you die permanently.</li>
        <li>
          While Bleeding Out, you are mortally wounded, and cannot attack, cast
          spells, or use special abilities - your injuries are simply too great!
        </li>
        <li>
          While Bleeding Out, you must roleplay being gravely wounded —
          crawling, calling for help, etc.
        </li>
        <li>
          Another character with the Practical Medicine skill can stabilise you,
          restoring you to 1 HP and ending Bleeding Out.
        </li>
        <li>
          The Heal spell cast on you immediately ends Bleeding Out (even without
          Practical Medicine).
        </li>
        <li>If the 5-minute timer expires, your character dies.</li>
      </ul>

      <hr className="my-6 border-border" />

      <h2 className="text-xl font-semibold mt-6 mb-2">Death</h2>
      <p className="text-sm text-muted-foreground mb-3">
        When your character dies, put on a blue headband to enter the Ghost
        World as a spirit. You have a limited amount of time (decided by GMs) to
        attempt to return to life through in-game means before your character is
        permanently dead.
      </p>
      <p className="text-sm text-muted-foreground mb-3">
        Possible means of returning to life include:
      </p>
      <ul className="list-disc list-inside text-sm text-muted-foreground mb-3 space-y-1">
        <li>
          Having a Phylactery (from the Gift of Life Everlasting) — allows
          automatic return from death
        </li>
        <li>A powerful ritual performed by other characters</li>
        <li>Patron intervention (at a cost — likely a new Dark Offering)</li>
      </ul>
      <p className="text-sm text-muted-foreground mb-3">
        If you cannot return to life in time, your character is permanently
        dead. Speak to a GM about creating a new character.
      </p>

      <hr className="my-6 border-border" />

      <h2 className="text-xl font-semibold mt-6 mb-2">The Ghost World</h2>
      <p className="text-sm text-muted-foreground mb-3">
        Another dimension overlaps the physical world. The Ghost World is where
        spirits pass when their mortal forms die... though sometimes, they do
        not pass on entirely.
      </p>
      <p className="text-sm text-muted-foreground mb-3">
        Your spirit can persist.
      </p>
      <p className="text-sm text-muted-foreground mb-3">
        Creatures in the Ghost World are represented in game by wearing a
        clearly visible blue headband.
      </p>
      <p className="text-sm text-muted-foreground mb-3">
        Once you die, place a blue headband on. If you can, place a mannequin or
        marker where your physical body lies, as your spirit wanders in search
        of resurrection.
      </p>
      <p className="text-sm text-muted-foreground mb-3">
        While you are Ghostly, your character has 1 HP, which cannot be
        increased in any way. If you lose this HP, you must immediately return
        to your physical body. If your true body is already dead, your character
        is permanently killed!
      </p>
      <p className="text-sm text-muted-foreground mb-3">
        While in the Ghost World, you cannot touch or interact with anyone or
        anything in the physical world.
      </p>
      <p className="text-sm text-muted-foreground mb-3">
        You are invisible to anyone in the physical world, except if your
        reflection is seen through a mirror, and you cannot be heard, except for
        faint whispers. However, you can clearly see and hear everything that
        happens around you.
      </p>
      <p className="text-sm text-muted-foreground mb-3">
        As a ghost, you may call out to any Patron who may be listening, and
        make a deal - in exchange for a Dark Offering, they will surely be
        willing to return you to life. If you do this, you now have access to
        that Patron&apos;s Challenges if you do not already, and may immediately
        return to your physical body, fully healed.
      </p>
      <p className="text-sm text-muted-foreground mb-3">
        If this price is too steep for you, many Patrons accept flesh and blood
        instead of souls as payment for this service - but not yours, as your
        dead body is currently worthless. If you can somehow contact one of the
        living - and somehow convince them to reduce their max HP score by 1 as
        a blood sacrifice - then any Patron will be happy to resurrect you!
      </p>
      <p className="text-sm text-muted-foreground mb-3">
        The willing donor must roleplay the blood sacrifice for at least 1
        minute. During this ritual they must say your true name, and the name of
        the Patron receiving their gift. If they do this, you may immediately
        return to your physical body, fully healed. The blood donor does not
        gain any benefits from this ritual!
      </p>
      <p className="text-sm text-muted-foreground mb-3">
        If you choose not to resurrect within 1 hour of Bleeding Out, your
        character is permanently dead, and the scattered pieces of your soul
        will feed the hungry Patrons that possess them.
      </p>

      <hr className="my-6 border-border" />

      <h2 className="text-xl font-semibold mt-6 mb-2">
        Poisons &amp; Conditions
      </h2>

      <Card className="mb-3">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Poisoned</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Your maximum HP is reduced to 1 until cured. Cure with a Long Rest, or
          with a character using Practical Medicine (costs 1 silver coin for
          antivenom).
        </CardContent>
      </Card>

      <Badge variant="outline">Blue headband = Ghost World / Dead</Badge>
    </RulebookPage>
  );
}
